'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { detectUserCurrency, getPricingForCurrency } from '@/lib/calculator-utils';
import type { SupportedCurrency } from '@/lib/env';
import type { PricingConfig } from '@/types/calculator';

interface SettingsState {
  currency: SupportedCurrency;
  pricing: PricingConfig;
}

interface SettingsContextType {
  currency: SupportedCurrency;
  pricing: PricingConfig;
  setCurrency: (currency: SupportedCurrency) => void;
  setPricing: (pricing: PricingConfig) => void;
  updatePricing: <K extends keyof PricingConfig>(field: K, value: PricingConfig[K]) => void;
  resetPricingToDefaults: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const LEGACY_STORAGE_KEY = 'accessnow-calculator-settings';
const STORAGE_KEY_PREFIX = 'accessnow-calculator-custom-pricing';

// Helper to generate storage key for currency
function getStorageKey(currency: SupportedCurrency): string {
  return `${STORAGE_KEY_PREFIX}-${currency}`;
}

// Helper to check if pricing differs from defaults
function pricingDiffersFromDefaults(
  pricing: PricingConfig,
  defaults: PricingConfig
): boolean {
  return Object.keys(defaults).some(key => {
    const typedKey = key as keyof typeof defaults;
    return pricing[typedKey] !== defaults[typedKey];
  });
}

// Helper to get only the fields that differ from defaults
function getCustomPricingFields(
  pricing: PricingConfig,
  defaults: PricingConfig
): Partial<PricingConfig> {
  const customFields: Partial<PricingConfig> = {};
  Object.keys(defaults).forEach(key => {
    const typedKey = key as keyof PricingConfig;
    if (pricing[typedKey] !== defaults[typedKey]) {
      customFields[typedKey] = pricing[typedKey];
    }
  });
  return customFields;
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SettingsState>(() => {
    // Initialize with defaults
    const currency = 'NZD';
    return {
      currency,
      pricing: getPricingForCurrency(currency),
    };
  });

  const [isInitialized, setIsInitialized] = useState(false);

  // Load settings from localStorage on mount (only once)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        // Check for legacy format first and migrate if needed
        const legacySettings = localStorage.getItem(LEGACY_STORAGE_KEY);
        let detectedCurrency = detectUserCurrency();
        
        if (legacySettings) {
          try {
            // Migrate legacy format with defensive parsing
            const parsedLegacy = JSON.parse(legacySettings);
            
            // Validate that parsedLegacy is an object and not null
            if (parsedLegacy && typeof parsedLegacy === 'object' && !Array.isArray(parsedLegacy)) {
              // Safely extract currency
              if (typeof parsedLegacy.currency === 'string') {
                detectedCurrency = parsedLegacy.currency;
              }
              
              // Save legacy v3 custom pricing to new format if it exists and differs from defaults
              if (parsedLegacy.pricingV3 && typeof parsedLegacy.pricingV3 === 'object') {
                try {
                  const defaults = getPricingForCurrency(detectedCurrency);
                  if (pricingDiffersFromDefaults(parsedLegacy.pricingV3, defaults)) {
                    const customFields = getCustomPricingFields(parsedLegacy.pricingV3, defaults);
                    localStorage.setItem(getStorageKey(detectedCurrency), JSON.stringify(customFields));
                  }
                } catch (error) {
                  console.warn('Failed to migrate V3 pricing, skipping:', error);
                }
              }
            } else {
              console.warn('Legacy settings data is not a valid object, clearing');
            }
          } catch (parseError) {
            console.warn('Failed to parse legacy settings JSON, clearing corrupted data:', parseError);
          }
          
          // Always remove legacy key after attempting migration
          localStorage.removeItem(LEGACY_STORAGE_KEY);
        }
        
        // Load custom pricing for current currency
        const loadCustomPricing = (
          defaults: PricingConfig,
          currency: SupportedCurrency
        ): PricingConfig => {
          try {
            const customData = localStorage.getItem(getStorageKey(currency));
            if (customData) {
              const custom = JSON.parse(customData);
              return { ...defaults, ...custom };
            }
          } catch (error) {
            console.warn(`Failed to load custom pricing:`, error);
          }
          return defaults;
        };
        
        const defaults = getPricingForCurrency(detectedCurrency);
        setSettings({
          currency: detectedCurrency,
          pricing: loadCustomPricing(defaults, detectedCurrency),
        });
        
      } catch (error) {
        console.warn('Failed to load settings from localStorage:', error);
        // Fallback to detecting currency
        const detectedCurrency = detectUserCurrency();
        setSettings({ 
          currency: detectedCurrency,
          pricing: getPricingForCurrency(detectedCurrency)
        });
      }
      setIsInitialized(true);
    }
  }, []);

  // Save custom pricing to localStorage whenever it changes
  useEffect(() => {
    if (isInitialized && typeof window !== 'undefined') {
      try {
        const defaults = getPricingForCurrency(settings.currency);
        const storageKey = getStorageKey(settings.currency);
        
        if (pricingDiffersFromDefaults(settings.pricing, defaults)) {
          const customFields = getCustomPricingFields(settings.pricing, defaults);
          localStorage.setItem(storageKey, JSON.stringify(customFields));
        } else {
          // Remove custom pricing if it matches defaults
          localStorage.removeItem(storageKey);
        }
      } catch (error) {
        console.warn('Failed to save custom pricing to localStorage:', error);
      }
    }
  }, [settings, isInitialized]);

  const setCurrency = useCallback((currency: SupportedCurrency) => {
    if (typeof window !== 'undefined') {
      try {
        // Load custom pricing for the new currency
        const loadCustomPricing = (
          defaults: PricingConfig,
          currency: SupportedCurrency
        ): PricingConfig => {
          try {
            const customData = localStorage.getItem(getStorageKey(currency));
            if (customData) {
              const custom = JSON.parse(customData);
              return { ...defaults, ...custom };
            }
          } catch (error) {
            console.warn(`Failed to load custom pricing:`, error);
          }
          return defaults;
        };
        
        const defaults = getPricingForCurrency(currency);
        setSettings({
          currency,
          pricing: loadCustomPricing(defaults, currency),
        });
      } catch (error) {
        console.warn('Failed to load custom pricing for new currency:', error);
        setSettings({ 
          currency,
          pricing: getPricingForCurrency(currency)
        });
      }
    } else {
      setSettings({ 
        currency,
        pricing: getPricingForCurrency(currency)
      });
    }
  }, []);

  const setPricing = (pricing: PricingConfig) => {
    setSettings(prev => ({ ...prev, pricing }));
  };

  const updatePricing = <K extends keyof PricingConfig>(
    field: K,
    value: PricingConfig[K]
  ) => {
    setSettings(prev => ({
      ...prev,
      pricing: { ...prev.pricing, [field]: value }
    }));
  };

  const resetPricingToDefaults = useCallback(() => {
    // Remove custom pricing from localStorage and reset to defaults
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(getStorageKey(settings.currency));
      } catch (error) {
        console.warn('Failed to remove custom pricing from localStorage:', error);
      }
    }
    setSettings(prev => ({
      ...prev,
      pricing: { ...getPricingForCurrency(prev.currency) }
    }));
  }, [settings.currency]);

  return (
    <SettingsContext.Provider
      value={{
        currency: settings.currency,
        pricing: settings.pricing,
        setCurrency,
        setPricing,
        updatePricing,
        resetPricingToDefaults,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
